<script setup lang="ts">
import { create } from '@bufbuild/protobuf';
import { Code } from '@connectrpc/connect';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
  GpBtn,
  GpPasswordInput,
  GpTextInput,
  PasswordStrength,
  usePasswordValidation,
} from '@gportal/uikit';
import useGlobalConfig from '@panel/composables/useGlobalConfig';
import type { ServiceError } from 'gpcore-apikit/grpc/types';
import { useSessionStore } from 'gpcore-apikit/store/pinia/session';
import { useUserStore } from 'gpcore-apikit/store/pinia/user';
import { RegisterRequestSchema } from 'gpcore-grpc/gpcore/api/auth/v1/requests_pb';
import ExternalLink from 'gpcore-uikit/components/Component.ExternalLink.vue';
import ConfirmModal from 'gpcore-uikit/components/new/ConfirmModal.vue';
import useNotifications from 'gpcore-uikit/composables/useNotifications';
import { useField, useForm } from 'vee-validate';
import { computed, onMounted, ref, watchEffect } from 'vue';
import { useReCaptcha } from 'vue-recaptcha-v3';
import { useRouter } from 'vue-router';
import { object as yupObject, ref as yupRef, string as yupString } from 'yup';

const userStore = useUserStore();
const { addNotification } = useNotifications();
const recaptcha = useReCaptcha();

const { errors, handleSubmit } = useForm<{
  email: string;
  password: string;
  passwordRepeat: string;
  firstName: string;
  lastName: string;
}>({
  validationSchema: yupObject({
    firstName: yupString().required(),
    lastName: yupString().required(),
    email: yupString().required(),
    password: yupString().required('Password is required.').min(8),
    passwordRepeat: yupString()
      .required('Password repeat is required.')
      .oneOf([yupRef('password')], 'Passwords do not match!'),
  }),
});

const {
  value: firstName,
  setTouched: setFirstNameTouched,
  meta: firstNameMeta,
} = useField<string>('firstName', null, {
  initialValue: '',
});
const {
  value: lastName,
  setTouched: setLastNameTouched,
  meta: lastNameMeta,
} = useField<string>('lastName', null, {
  initialValue: '',
});
const {
  value: email,
  setTouched: setEmailTouched,
  meta: emailMeta,
} = useField<string>('email', null, {
  initialValue: '',
});
const {
  value: password,
  setTouched: setPasswordTouched,
  meta: passwordMeta,
} = useField<string>('password', null, {
  initialValue: '',
});
const {
  value: passwordRepeat,
  setTouched: setPasswordRepeatTouched,
  meta: passwordRepeatMeta,
} = useField<string>('passwordRepeat', null, {
  initialValue: '',
});

watchEffect(() => {
  setFirstNameTouched(firstName.value.length > 0 || firstNameMeta.touched);
  setLastNameTouched(lastName.value.length > 0 || lastNameMeta.touched);
  setEmailTouched(email.value.length > 0 || emailMeta.touched);
  setPasswordTouched(password.value.length > 0 || passwordMeta.touched);
  setPasswordRepeatTouched(passwordRepeat.value.length > 0 || passwordRepeatMeta.touched);
});

const allFieldsTouched = computed<boolean>(
  () => emailMeta.touched && passwordMeta.touched && passwordMeta.touched,
);

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const { strength } = usePasswordValidation(password, 200);
const hintModel = computed<{ message: string; hintClass: string }>(() => {
  if (strength.value === 0) {
    return {
      hintClass: /* tw */ 'tw-text-gp-crab',
      message: 'weak password',
    };
  }

  if (strength.value === 1) {
    return {
      hintClass: /* tw */ 'tw-text-gp-orange',
      message: 'medium password',
    };
  }

  if (strength.value === 2) {
    return {
      hintClass: /* tw */ 'tw-text-gp-green',
      message: 'strong password',
    };
  }

  return undefined;
});
const isStrongPassword = computed<boolean>(() => strength.value === PasswordStrength.STRONG);

const router = useRouter();
const sessionStore = useSessionStore();

const isSubmitting = ref<boolean>(false);
const config = useGlobalConfig();
const isAlreadyExistsModalOpen = ref<boolean>(false);

const isUserExistsInDB = ref<boolean>(false);
const loginUri = ref<string>('');
const getLoginUri = async () => {
  let redirectTo = 'Welcome';
  // redirect to home, if user exists in DB after login
  if (isUserExistsInDB.value) {
    redirectTo = 'Home';
  }

  const route = router.resolve({ name: redirectTo });

  loginUri.value = await sessionStore.keycloak.createLoginUrl({
    redirectUri: new URL(route.href, window.location.origin).href,
  });
};
const goToLogin = () => {
  window.location.href = loginUri.value;
};
const submitForm = handleSubmit(async (values) => {
  try {
    isSubmitting.value = true;

    let token = '';
    if (config.recaptcha.publicKey != null && config.recaptcha.publicKey !== '') {
      await recaptcha.recaptchaLoaded();
      token = await recaptcha.executeRecaptcha('register');
    }

    const request = create(RegisterRequestSchema);
    request.email = values.email;
    request.password = values.password;
    request.recaptchaToken = token;
    request.firstName = values.firstName;
    request.lastName = values.lastName;

    await userStore.register(request);

    // after registration, we go to welcome
    goToLogin();
  } catch (e) {
    const error = e as ServiceError;

    if (error.code === Code.AlreadyExists) {
      isAlreadyExistsModalOpen.value = true;
      // depending on, if the user exists in DB, we redirect to home after login
      isUserExistsInDB.value = error.message.includes('database');
      return;
    }

    addNotification({
      type: 'danger',
      message: (e as ServiceError).message,
    });
  } finally {
    isSubmitting.value = false;
    isUserExistsInDB.value = false;
  }
});

onMounted(() => {
  getLoginUri();
});
</script>

<template>
  <form @submit.prevent="submitForm">
    <GpTextInput
      class="tw-mb-4"
      v-model="firstName"
      input-id="register__first_name"
      name="register__first_name"
      label="First name"
      :error="errors.firstName"
      transparent
    />

    <GpTextInput
      class="tw-mb-4"
      v-model="lastName"
      input-id="register__last_name"
      name="register__last_name"
      label="Last name"
      :error="errors.lastName"
      transparent
    />

    <GpTextInput
      class="tw-mb-4"
      v-model="email"
      input-id="register__email"
      name="register__email"
      autocomplete="email"
      type="email"
      label="E-mail"
      :error="errors.email"
      transparent
    />

    <GpPasswordInput
      class="tw-mb-4"
      v-model="password"
      input-id="register__password"
      name="register__password"
      autocomplete="new-password"
      type="password"
      label="Password"
      transparent
    >
      <template #hint>
        <p
          v-if="errors.password"
          class="tw-mb-0 tw-mt-2 tw-text-sm tw-text-gp-crab"
          v-playwright="`signup-password-error`"
        >
          {{ errors.password }}
        </p>
        <p
          v-else-if="hintModel != null"
          class="tw-mb-0 tw-mt-2 tw-text-sm"
          :class="hintModel.hintClass"
        >
          <FontAwesomeIcon v-if="isStrongPassword" :icon="['fas', 'fa-check']" class="tw-mr-1" />
          <FontAwesomeIcon v-else :icon="['fas', 'fa-circle-exclamation']" class="tw-mr-1" />
          {{ hintModel.message }}
        </p>
      </template>
    </GpPasswordInput>

    <GpPasswordInput
      class="tw-mb-8"
      v-model="passwordRepeat"
      input-id="register__password-repeat"
      name="register__password-repeat"
      autocomplete="new-password"
      type="password"
      label="Repeat password"
      :error="errors.passwordRepeat"
      transparent
    />

    <p class="tw-mb-4">
      By clicking on "Create account", you confirm that you have read our
      <ExternalLink url="https://gpcore.io/terms-and-conditions">terms and conditions</ExternalLink>
      and
      <ExternalLink url="https://gpcore.io/privacy-policy">privacy policy</ExternalLink>
      and accepted them.
    </p>

    <GpBtn
      class="tw-block tw-w-full"
      :disabled="Object.keys(errors).length > 0 || !allFieldsTouched || isSubmitting"
      type="submit"
      v-playwright="`register-submit`"
    >
      Create account
    </GpBtn>

    <ConfirmModal
      :show="isAlreadyExistsModalOpen"
      question="User with this email already exists. Do you want to log in?"
      confirm-text="Log in"
      @confirm="goToLogin"
    />
  </form>
</template>

<style scoped></style>
